import React from 'react';
import SettingsContainer from '@dashboard/components/SettingsContainer';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import SettingsDescription from '@dashboard/components/SettingsDescription';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import PageHeader from '@dashboard/components/PageHeader';
import { Form } from '@dashboard/components/Formik';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import Link from '@dashboard/components/Link/Link';
import PaymentMethodDomains from './components/PaymentMethodDomains';

const SettingsPayments = () => {
  const { seller } = useAuth();

  const { updateSeller } = useSellerQueries();

  return (
    <div className="p-4 space-y-4">
      <PageHeader
        title="Payments"
        description="Manage store-wide payment settings, prevent abuse and configure payment method domains."
      />

      <div className="mb-32">
        <SettingsContainer size="lg">
          <SettingsHeader id="payments">Payment settings</SettingsHeader>
          <SettingsDescription>Enable payment methods and prevent abuse.</SettingsDescription>
          <Form
            enableReinitialize
            initialValues={{
              paymentGateways: seller.paymentGateways && {
                ...seller.paymentGateways,
              },
              captcha: seller.captcha,
              limitSubscriptions: seller.limitSubscriptions,
              checkoutAbandonment: seller.checkoutAbandonment,
            }}
            fields={({ values }) => [
              {
                name: 'paymentGateways.stripe.enableAppleGooglePay',
                required: false,
                type: 'checkbox',
                label: 'Enable Apple & Google Pay',
              },
              {
                name: 'captcha.recaptcha.enabled',
                required: false,
                type: 'checkbox',
                label: 'Enable Google reCAPTCHA v3',
                tooltip:
                  'Google reCAPTCHA helps prevent fraudulent payments on your checkout pages by verifying that the person making the payment is a human.',
              },
              {
                name: 'limitSubscriptions.enabled',
                required: false,
                type: 'checkbox',
                label:
                  'Prevent customers from subscribing if they already have a subscription in Checkout Page',
                reduceMargin: values.limitSubscriptions?.enabled,
                tooltip: `Applies to all checkout pages — If the customer email already has an active subscription within Checkout Page, the customer will be shown a message that they can't start another subscription.`,
              },
              {
                name: 'limitSubscriptions.limitSubscriptionsStripe.enabled',
                required: false,
                type: 'checkbox',
                label:
                  'Prevent customers from subscribing if they already have a subscription in Stripe',
                hidden: !values.limitSubscriptions?.enabled,
                tooltip: `If the customer email already has an active subscription in Stripe, the customer will be shown a message that they can't start another subscription.`,
                inset: true,
              },
            ]}
            onSubmit={async values => {
              try {
                const { data } = await updateSeller(values);

                return {
                  data,
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
          />
        </SettingsContainer>

        <SettingsContainer size="lg">
          <SettingsDivider />
        </SettingsContainer>

        <SettingsContainer size="lg">
          <SettingsHeader id="payment-method-domains">Payment method domains</SettingsHeader>
          <SettingsDescription className="mb-4">
            Add your website's domain(s) to enable Apple Pay and Google Pay on checkout embeds and
            pop-ups.{' '}
            <Link
              href="/docs/articles/accept-payments-with-card-apple-pay-google-pay"
              color="default"
              blank
            >
              Learn more
            </Link>
          </SettingsDescription>
          <PaymentMethodDomains />
        </SettingsContainer>
      </div>
    </div>
  );
};

export default SettingsPayments;
